import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { oltAPI, settingsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import {
  Router, Activity, AlertTriangle, RefreshCw, Trash2,
  Plus, Search, Wifi, Zap, Server, X,
  Edit2, Settings, RotateCcw, Tag, Check, Download, Power, PowerOff, UploadCloud
} from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import { useViewMode } from '../hooks/useViewMode';
import OltMobileView from './olt/OltMobileView';
import { HealthDonut, PonUsageChart, SignalHistogram } from './olt/OltCharts';
import './olt/oltWeb.css';

export const fmtBytes = bytes => {
  const gb = (bytes || 0) / 1e9;
  return gb >= 1 ? `${gb.toFixed(2)} GB` : `${((bytes || 0) / 1e6).toFixed(0)} MB`;
};

// OLT reports adminState as "enable" / "disable" (not "enabled"/"disabled") — matching
// on the exact string here since that mismatch previously left the button stuck on
// "Deactivate" forever, never flipping to "Activate" after a router was disabled.
export const isOnuEnabled = (o) => (o.adminState || '').toLowerCase() !== 'disable';

export const isOnuOnline = (o) => (o.status || '').toLowerCase().includes('online') || o.online === true;
export const onuRx = (o) => o.rxPower ?? o.rx_power ?? o.opticalRx;
const WEAK_DBM = -24;
export const isOnuWeak = (o) => {
  if (!isOnuOnline(o)) return false;
  const v = parseFloat(onuRx(o));
  return Number.isFinite(v) && v < WEAK_DBM;
};

// ─── Signal strength badge ────────────────────────────────────────────────────
export function SignalBadge({ dbm }) {
  if (dbm == null || dbm === '') return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const val = parseFloat(dbm);
  let color = 'var(--c-065f46)', bg = 'var(--cb-d1fae5)';
  if (val < -27) { color = 'var(--c-991b1b)'; bg = 'var(--cb-fee2e2)'; }
  else if (val < -24) { color = 'var(--c-92400e)'; bg = 'var(--cb-fef3c7)'; }
  return (
    <span className="badge" style={{ background: bg, color }}>
      {val} dBm
    </span>
  );
}

// ─── Inline Label Editor ──────────────────────────────────────────────────────
function LabelCell({ onu, isAdmin, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const currentLabel = onu.label || onu.name || onu.description || '';

  const startEdit = () => {
    setValue(currentLabel);
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const cancel = () => { setEditing(false); setValue(''); };

  const save = async () => {
    if (value === currentLabel) { cancel(); return; }
    setSaving(true);
    try {
      const res = await oltAPI.saveONULabel(onu.id, value);
      onSave(onu.id, value);
      if (res.data?.oltSynced === false) {
        toast.error(res.data?.message || 'Label saved locally, but OLT was not updated', { duration: 6000 });
      } else {
        toast.success(res.data?.message || 'Label saved');
      }
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') save();
    else if (e.key === 'Escape') cancel();
  };

  if (editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 160 }}>
        <input
          ref={inputRef}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          style={{
            flex: 1, fontSize: '.82rem', padding: '2px 6px', borderRadius: 4,
            border: '1px solid var(--primary)', outline: 'none', minWidth: 0,
          }}
        />
        <button className="btn btn-primary btn-sm" style={{ padding: '2px 6px' }}
          onClick={save} disabled={saving} title="Save">
          <Check size={11} />
        </button>
        <button className="btn btn-secondary btn-sm" style={{ padding: '2px 6px' }}
          onClick={cancel} title="Cancel">
          <X size={11} />
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <span style={{ fontSize: '.83rem', fontWeight: 600 }}>{currentLabel || <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>—</span>}</span>
      {isAdmin && (
        <button onClick={startEdit} title="Edit label"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--text-muted)', lineHeight: 1, opacity: 0.6 }}>
          <Edit2 size={11} />
        </button>
      )}
    </div>
  );
}

function OnuActionButtons({ onu, isAdmin, deactivating, oltLink, onProfile, onUpgrade, onReboot, onToggle, onDeregister }) {
  if (!isAdmin) return null;
  const enabled = isOnuEnabled(onu);
  return (
    <div className="ow-onu-actions">
      <button className="btn btn-secondary btn-sm" title="ONU Config (opens OLT interface)" onClick={() => {
        const url = oltLink(`onuconfigsrv.html?ponid=${onu.port}&onuid=${onu.onuId}`);
        if (url) window.open(url, 'olt-config', 'width=1100,height=750,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no');
        else toast.error('OLT web URL not available');
      }}>
        <Settings size={12} />
      </button>
      <button className="btn btn-secondary btn-sm" title={onu.linePro ? `Line: ${onu.linePro} · Srv: ${onu.srvPro || 'N/A'} — click to change` : 'Set Line / Service Profile'} onClick={() => onProfile(onu)}>
        <Tag size={12} />
        {onu.linePro && <span style={{ fontSize: '.72rem', maxWidth: 64, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{onu.linePro}</span>}
      </button>
      <button className="btn btn-secondary btn-sm" title="Upgrade ONU Firmware" onClick={() => onUpgrade(onu)}>
        <UploadCloud size={12} />
      </button>
      <button className="btn btn-warning btn-sm" title="Restart ONU" onClick={() => onReboot(onu)}>
        <RotateCcw size={12} />
      </button>
      <button className="btn btn-sm" title={enabled ? 'Deactivate ONU' : 'Activate ONU'} onClick={() => onToggle(onu)} disabled={!!deactivating[onu.id]}
        style={{
          background: enabled ? 'var(--cb-fef3c7)' : 'var(--cb-d1fae5)',
          color: enabled ? 'var(--c-92400e)' : 'var(--c-065f46)',
          border: enabled ? '1px solid var(--cb-fcd34d)' : '1px solid var(--cb-6ee7b7)',
        }}>
        {enabled ? <PowerOff size={12} /> : <Power size={12} />}
      </button>
      <button className="btn btn-danger btn-sm" title="Deregister" onClick={() => onDeregister(onu)}>
        <Trash2 size={12} />
      </button>
    </div>
  );
}

// ─── Profile Modal ────────────────────────────────────────────────────────────
// Line/Service profile options and the ONU's currently-bound profile are fetched
// live from the OLT (GET .../profile) rather than trusted from cached DB fields —
// the "Profile" shown elsewhere on an ONU row is actually ONU *Type* (always
// "default"), a different field from the Line/Service profile this modal edits.
export function ProfileModal({ onu, onClose, onSuccess }) {
  const [loading, setLoading]   = useState(true);
  const [lineOptions, setLineOptions] = useState([]);
  const [srvOptions, setSrvOptions]   = useState([]);
  const [linePro, setLinePro]   = useState('N/A');
  const [srvPro, setSrvPro]     = useState('N/A');
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    let cancelled = false;
    oltAPI.getONUProfile(onu.port, onu.onuId)
      .then(({ data }) => {
        if (cancelled) return;
        const d = data.data;
        setLineOptions(d.lineOptions);
        setSrvOptions(d.srvOptions);
        setLinePro(d.lineProfile);
        setSrvPro(d.srvProfile);
      })
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load current profile from OLT'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [onu.port, onu.onuId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await oltAPI.changeONUProfile(onu.port, onu.onuId, {
        onuGponId: onu.id,
        linePro,
        srvPro,
      });
      toast.success('Profile updated successfully');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Profile update failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h3>Edit ONU Profile</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        {loading ? (
          <div className="modal-body" style={{ textAlign: 'center', padding: 30 }}>
            <div className="spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: '.82rem' }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{onu.id}</div>
                <div style={{ color: 'var(--text-muted)' }}>
                  {onu.serialNumber && <span className="font-mono">{onu.serialNumber}</span>}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Line Profile</label>
                <select className="form-control" value={linePro} onChange={e => setLinePro(e.target.value)}>
                  {lineOptions.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Service Profile</label>
                <select className="form-control" value={srvPro} onChange={e => setSrvPro(e.target.value)}>
                  {srvOptions.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div style={{ padding: '0 24px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Updating…' : 'Apply Profile'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Provision ONU Modal ──────────────────────────────────────────────────────
export function ProvisionModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    serialNumber: '', macAddress: '', port: '0',
    onuType: '', vlanId: '', profile: '', name: '', description: ''
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await oltAPI.provision(form);
      toast.success('ONU provisioned successfully');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Provisioning failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>Provision New ONU</h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Serial Number <span className="required">*</span></label>
                <input className="form-control font-mono" value={form.serialNumber}
                  onChange={e => set('serialNumber', e.target.value.toUpperCase())} required
                  placeholder="e.g. SYRO12345678" />
              </div>
              <div className="form-group">
                <label className="form-label">MAC Address</label>
                <input className="form-control font-mono" value={form.macAddress}
                  onChange={e => set('macAddress', e.target.value.toUpperCase())}
                  placeholder="XX:XX:XX:XX:XX:XX" />
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">PON Port <span className="required">*</span></label>
                <input className="form-control" value={form.port} onChange={e => set('port', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">VLAN ID</label>
                <input className="form-control" value={form.vlanId} onChange={e => set('vlanId', e.target.value)} placeholder="e.g. 100" />
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">ONU Type / Model</label>
                <input className="form-control" value={form.onuType} onChange={e => set('onuType', e.target.value)} placeholder="e.g. GPON-ONT" />
              </div>
              <div className="form-group">
                <label className="form-label">Service Profile</label>
                <input className="form-control" value={form.profile} onChange={e => set('profile', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Label / Customer Name</label>
              <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea className="form-control" rows={2} value={form.description} onChange={e => set('description', e.target.value)} />
            </div>
          </div>
          <div style={{ padding: '0 24px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Provisioning…' : 'Provision ONU'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Upgrade Modal ────────────────────────────────────────────────────────────
export function UpgradeModal({ onu, onClose, onSuccess }) {
  const [firmwareVersion, setFirmwareVersion] = useState('');
  const [upgrading, setUpgrading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpgrading(true);
    try {
      await oltAPI.upgradeONU(onu.slot, onu.port, onu.onuId, { firmwareVersion: firmwareVersion.trim() || undefined });
      toast.success('Firmware upgrade initiated — ONU will reboot during upgrade');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upgrade failed');
    } finally { setUpgrading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UploadCloud size={16} color="var(--primary)" /> ONU Firmware Upgrade
          </h3>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* ONU info */}
            <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: '.82rem' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{onu.id}</div>
              <div style={{ color: 'var(--text-muted)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {onu.serialNumber && <span className="font-mono">{onu.serialNumber}</span>}
                {onu.model        && <span>{onu.model}</span>}
              </div>
            </div>

            {/* Warning */}
            <div style={{ background: 'var(--cb-fef3c7)', border: '1px solid var(--cb-fcd34d)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: '.82rem', color: 'var(--c-92400e)', display: 'flex', gap: 8 }}>
              <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>The ONU will reboot during upgrade and may lose connectivity for 1–5 minutes.</span>
            </div>

            <div className="form-group">
              <label className="form-label">Firmware Version <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
              <input
                className="form-control font-mono"
                placeholder="Leave blank to use OLT default"
                value={firmwareVersion}
                onChange={e => setFirmwareVersion(e.target.value)}
              />
              <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                Specify a version only if your OLT has multiple firmware images loaded.
              </div>
            </div>
          </div>
          <div style={{ padding: '0 24px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={upgrading}>
              {upgrading ? 'Upgrading…' : 'Start Upgrade'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── GE Port Card ─────────────────────────────────────────────────────────────
function GEPortCard({ port }) {
  const [hovered, setHovered] = useState(false);
  const isUp = (port?.status || '').toLowerCase() === 'up';

  const rxBytes = port?.rxBytes ?? null;
  const txBytes = port?.txBytes ?? null;
  const errors  = port?.errors  ?? 0;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        background: isUp ? 'var(--card-bg)' : 'var(--bg)',
        border: `2px solid ${isUp ? 'var(--cb-10b981)' : 'var(--cb-e5e7eb)'}`,
        borderRadius: 10,
        padding: '14px 16px',
        textAlign: 'center',
        cursor: 'default',
        transition: 'box-shadow .15s, border-color .15s',
        boxShadow: hovered ? '0 4px 16px rgba(0,0,0,.10)' : '0 1px 3px rgba(0,0,0,.06)',
        minWidth: 110,
      }}
    >
      {/* Port icon */}
      <div style={{
        width: 36, height: 36, borderRadius: '50%', margin: '0 auto 8px',
        background: isUp ? 'var(--cb-d1fae5)' : 'var(--cb-f3f4f6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Activity size={18} color={isUp ? 'var(--c-065f46)' : 'var(--c-9ca3af)'} />
      </div>

      {/* Port name */}
      <div style={{ fontWeight: 700, fontSize: '.9rem', marginBottom: 4 }}>{port?.name || port?.id || '—'}</div>
      {port?.name && <div style={{ fontSize: '.68rem', color: 'var(--text-muted)', marginTop: -3, marginBottom: 3 }}>{port.id}</div>}

      {/* Status chip */}
      <div style={{
        display: 'inline-block', padding: '2px 10px', borderRadius: 99, fontSize: '.72rem', fontWeight: 600,
        background: isUp ? 'var(--cb-d1fae5)' : 'var(--cb-f3f4f6)',
        color: isUp ? 'var(--c-065f46)' : 'var(--c-6b7280)',
      }}>{isUp ? 'UP' : 'DOWN'}</div>

      {/* Speed */}
      {port?.speed && (
        <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 5 }}>{port.speed}</div>
      )}

      {/* Hover tooltip */}
      {hovered && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)',
          background: 'var(--cb-1e293b)', color: 'var(--c-ffffff)', borderRadius: 8, padding: '10px 14px',
          fontSize: '.78rem', lineHeight: 1.7, whiteSpace: 'nowrap', zIndex: 50,
          boxShadow: '0 4px 20px rgba(0,0,0,.25)',
          pointerEvents: 'none',
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4, borderBottom: '1px solid rgba(255,255,255,.15)', paddingBottom: 4 }}>{port?.name ? `${port.name} (${port.id})` : port?.id}</div>
          {rxBytes != null && <div>↓ Rx: <b>{fmtBytes(rxBytes)}</b></div>}
          {txBytes != null && <div>↑ Tx: <b>{fmtBytes(txBytes)}</b></div>}
          {port?.rxPkts != null && <div>Rx Pkts: {port.rxPkts.toLocaleString()}</div>}
          {port?.txPkts != null && <div>Tx Pkts: {port.txPkts.toLocaleString()}</div>}
          {errors > 0 && <div style={{ color: 'var(--c-fca5a5)' }}>Errors: {errors}</div>}
          {rxBytes == null && txBytes == null && (
            <div style={{ color: 'var(--c-94a3b8)' }}>No traffic data</div>
          )}
          {/* tooltip arrow */}
          <div style={{
            position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
            borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
            borderTop: '6px solid var(--cb-1e293b)',
          }} />
        </div>
      )}
    </div>
  );
}

// ─── Main OLT Page ────────────────────────────────────────────────────────────
const MATCH_STATUS = {
  proposed:     { label: 'Match',             color: 'var(--c-059669)' },
  labelled:     { label: 'Labelled',          color: 'var(--c-059669)' },
  no_match:     { label: 'No match',          color: 'var(--c-6b7280)' },
  ambiguous:    { label: 'Several customers', color: 'var(--c-d97706)' },
  conflict:     { label: 'Conflict',          color: 'var(--c-d97706)' },
  write_failed: { label: 'Write failed',      color: 'var(--c-dc2626)' },
};

// Preview / result of "Match Blank ONUs by MAC". Proposed rows are tickable; everything else
// is shown with the reason it was left alone. Module scope so ticking doesn't remount it.
function BlankOnuMatchPanel({ result, selected, onToggle, onApply, applying, onClose }) {
  const plan = result.plan || [];
  const order = { proposed: 0, labelled: 0, write_failed: 1, conflict: 2, ambiguous: 3, no_match: 4 };
  const rows = [...plan].sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9));
  const proposed = plan.filter(p => p.status === 'proposed').length;
  return (
    <div style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)', padding: '10px 16px', fontSize: '.82rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <strong>{result.preview ? 'Preview' : 'Result'} — {result.blankCount} online ONU(s) blank or named after a closed/unknown customer</strong>
        {result.preview
          ? <span style={{ color: 'var(--c-059669)', fontWeight: 700 }}>{proposed} can be matched</span>
          : <span style={{ color: 'var(--c-059669)', fontWeight: 700 }}>{result.labelled} labelled</span>}
        {result.aborted && <span style={{ fontWeight: 700, color: 'var(--c-dc2626)' }}>{result.aborted}</span>}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {result.preview && proposed > 0 && (
            <button className="btn btn-primary btn-sm" onClick={onApply} disabled={applying || !selected.length}>
              {applying ? 'Labelling…' : `Apply ${selected.length} selected`}
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={applying}><X size={12} /></button>
        </span>
      </div>
      {rows.length > 0 && (
        <div style={{ maxHeight: 280, overflowY: 'auto', marginTop: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
                <th style={{ padding: 4 }} />
                <th style={{ padding: 4 }}>ONU</th>
                <th style={{ padding: 4 }}>Customer</th>
                <th style={{ padding: 4 }}>MAC</th>
                <th style={{ padding: 4 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(p => {
                const st = MATCH_STATUS[p.status] || { label: p.status, color: 'var(--c-374151)' };
                return (
                  <tr key={p.onuId} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: 4 }}>
                      {result.preview && p.status === 'proposed' && (
                        <input type="checkbox" checked={selected.includes(p.onuId)} onChange={() => onToggle(p.onuId)} disabled={applying} />
                      )}
                    </td>
                    <td style={{ padding: 4, whiteSpace: 'nowrap' }}>
                      {p.onuId}
                      <div style={{ color: 'var(--text-muted)' }}>{p.serialNumber}</div>
                      <div style={{ color: p.currentLabel ? 'var(--c-b45309)' : 'var(--text-muted)' }}>
                        {p.currentLabel ? `now "${p.currentLabel}" — ${p.why}` : 'blank'}
                      </div>
                    </td>
                    <td style={{ padding: 4 }}>{p.username ? <><strong>{p.username}</strong><div style={{ color: 'var(--text-muted)' }}>{p.customerName}</div></> : '—'}</td>
                    <td style={{ padding: 4, fontFamily: 'monospace' }}>
                      {p.mac || (p.macs || []).join(', ') || '—'}
                      {p.matchSource && <div style={{ color: 'var(--text-muted)', fontFamily: 'inherit' }}>{p.matchSource === 'live' ? 'live session' : 'Netzur stored'}</div>}
                    </td>
                    <td style={{ padding: 4 }}>
                      <span style={{ color: st.color, fontWeight: 700 }}>{st.label}</span>
                      {p.reason && <div style={{ color: 'var(--text-muted)' }}>{p.reason}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function OLTPage() {
  useDarkReady();
  // Reboot/provision/label-edit/upgrade actions match the backend's technicalOrAdmin gate.
  const { canManageHardware: isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab]           = useState('onus');
  const [sysInfo, setSysInfo]   = useState(null);
  const [oltCfg, setOltCfg]     = useState(null);
  const [onus, setOnus]         = useState([]);
  const [ports, setPorts]       = useState([]);
  const [gePorts, setGePorts]   = useState([]);
  const [alarms, setAlarms]     = useState([]);
  const [onusLoading, setOnusLoading]   = useState(false);
  const [portsLoading, setPortsLoading] = useState(false);
  const [geLoading, setGeLoading]       = useState(false);
  const [alarmsLoading, setAlarmsLoading] = useState(false);
  const [configured, setConfigured] = useState(null); // null=checking
  const [search, setSearch]     = useState(() => searchParams.get('serial') || '');

  // Sync search state when URL serial param changes (e.g. navigating /olt → /olt?serial=X → /olt)
  useEffect(() => {
    setSearch(searchParams.get('serial') || '');
  }, [searchParams]);
  const [showProvision, setShowProvision] = useState(false);
  const [oltWebURL, setOltWebURL] = useState('');
  const [profileModal, setProfileModal] = useState(null);
  const [upgradeModal, setUpgradeModal] = useState(null);
  const [selectedPort, setSelectedPort] = useState('all');
  const [offlineReason, setOfflineReason] = useState(null);
  const [onuView, setOnuView] = useState(() => localStorage.getItem('oltOnuView') || 'cards');
  const pickOnuView = (v) => { setOnuView(v); localStorage.setItem('oltOnuView', v); };
  const [savingConfig, setSavingConfig] = useState(false);
  const [fetchingAllOlt, setFetchingAllOlt] = useState(false);
  const [fetchAllOltResult, setFetchAllOltResult] = useState(null);
  const [fetchingAllOltMac, setFetchingAllOltMac] = useState(false);
  const [fetchAllOltMacResult, setFetchAllOltMacResult] = useState(null);
  const [macMatchSelected, setMacMatchSelected] = useState([]);
  // App View (phone/tablet bottom-tab shell) gets its own mobile layout, see olt/OltMobileView.js.
  // Same detection as CustomerDetailPage: the shell's saved choice, or a narrow screen regardless.
  // The technical login keeps its own saved choice.
  const inTechnical = useLocation().pathname.startsWith('/technical');
  const adminMode = useViewMode().viewMode;
  const techMode = useViewMode('technicalViewMode').viewMode;
  const [isNarrow, setIsNarrow] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isAppView = (inTechnical ? techMode : adminMode) === 'app' || isNarrow;
  // When each feed last loaded (shown as "Updated hh:mm") and whether the latest try failed.
  const [updated, setUpdated] = useState({});
  const [errors, setErrors]   = useState({});
  const mark = (k, err) => {
    if (!err) setUpdated(u => ({ ...u, [k]: Date.now() }));
    setErrors(e => ({ ...e, [k]: err || null }));
  };
  const [applyingMacMatch, setApplyingMacMatch] = useState(false);

  // ── Fetch OLT status + system info ──
  const fetchStatus = useCallback(async () => {
    try {
      const res = await oltAPI.getStatus();
      if (!res.data.configured) { setConfigured(false); return; }
      setConfigured(true);
      setSysInfo(res.data.data);
      if (res.data.webURL) setOltWebURL(res.data.webURL);
      mark('status');
    } catch (err) {
      setConfigured(false);
      mark('status', err.response?.data?.message || err.message || 'Cannot reach OLT');
      toast.error('Cannot reach OLT — check Settings');
    }
  }, []);

  const fetchONUs = useCallback(async (force = false) => {
    setOnusLoading(true);
    try {
      const res = await oltAPI.getONUs(force ? { refresh: 'true' } : undefined);
      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setOnus(list);
      mark('onus');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to load ONUs';
      mark('onus', msg);
      toast.error(msg);
    } finally { setOnusLoading(false); }
  }, []);

  const fetchPorts = useCallback(async () => {
    setPortsLoading(true);
    try {
      const res = await oltAPI.getPorts();
      setPorts(Array.isArray(res.data) ? res.data : res.data?.data || []);
      mark('ports');
    } catch (err) {
      mark('ports', err.response?.data?.message || 'Failed to load ports');
      toast.error(err.response?.data?.message || 'Failed to load ports');
    } finally { setPortsLoading(false); }
  }, []);

  const fetchGEPorts = useCallback(async () => {
    setGeLoading(true);
    try {
      const res = await oltAPI.getGEPorts();
      const raw = Array.isArray(res.data) ? res.data : res.data?.data || [];
      const map = {};
      for (const p of raw) map[p.id] = p;
      const filled = Array.from({ length: 8 }, (_, i) => {
        const id = `GE${i + 1}`;
        return map[id] || { id, status: 'unknown' };
      });
      setGePorts(filled);
      mark('ge');
    } catch (err) {
      mark('ge', err.response?.data?.message || err.message || 'Failed to load GE ports');
      setGePorts(Array.from({ length: 8 }, (_, i) => ({ id: `GE${i + 1}`, status: 'unknown' })));
    } finally { setGeLoading(false); }
  }, []);

  const fetchAlarms = useCallback(async () => {
    setAlarmsLoading(true);
    try {
      const res = await oltAPI.getAlarms();
      setAlarms(Array.isArray(res.data) ? res.data : res.data?.data || []);
      mark('alarms');
    } catch (err) {
      mark('alarms', err.response?.data?.message || 'Failed to load alarms');
      toast.error(err.response?.data?.message || 'Failed to load alarms');
    } finally { setAlarmsLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    if (tab === 'onus')    fetchONUs();
    else if (tab === 'ports')   fetchPorts();
    else if (tab === 'ge')      fetchGEPorts();
    else if (tab === 'alarms')  fetchAlarms();
  }, [tab, fetchONUs, fetchPorts, fetchGEPorts, fetchAlarms]);

  // ── Computed stats ──
  const totalONUs   = onus.length;
  const onlineONUs  = onus.filter(isOnuOnline).length;
  const offlineONUs = totalONUs - onlineONUs;
  const weakONUs    = onus.filter(isOnuWeak).length;
  const healthyONUs = Math.max(onlineONUs - weakONUs, 0);
  const offlineReasons = (() => {
    const m = {};
    for (const o of onus) {
      if (isOnuOnline(o)) continue;
      const r = (o.deregisterReason || '').trim() || 'Unknown';
      m[r] = (m[r] || 0) + 1;
    }
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  })();

  // Per-port stats for dropdown
  const portStats = {};
  for (const o of onus) {
    const p = o.port;
    if (p == null) continue;
    if (!portStats[p]) portStats[p] = { total: 0, online: 0 };
    portStats[p].total++;
    if (isOnuOnline(o)) portStats[p].online++;
  }
  const activePorts = Object.keys(portStats).map(Number).sort((a, b) => a - b);

  // ── ONU actions ──
  const handleReboot = async (o) => {
    if (!window.confirm(`Reboot ONU ${o.serialNumber || o.id}?`)) return;
    try {
      await oltAPI.rebootONU(o.slot, o.port, o.onuId || o.id);
      toast.success('Reboot command sent');
    } catch (err) { toast.error(err.response?.data?.message || 'Reboot failed'); }
  };

  const handleDeregister = async (o) => {
    if (!window.confirm(`Deregister ONU ${o.serialNumber || o.id}? This cannot be undone.`)) return;
    try {
      await oltAPI.deregisterONU(o.slot, o.port, o.onuId || o.id);
      toast.success('ONU deregistered');
      fetchONUs(true);
    } catch (err) { toast.error(err.response?.data?.message || 'Deregister failed'); }
  };

  const [deactivating, setDeactivating] = useState({});
  const handleToggleState = async (o) => {
    const isEnabled = isOnuEnabled(o);
    const action = isEnabled ? 'disable' : 'enable';
    const label  = isEnabled ? 'Deactivate' : 'Activate';
    if (!window.confirm(`${label} ONU ${o.serialNumber || o.id}?`)) return;
    setDeactivating(p => ({ ...p, [o.id]: true }));
    try {
      await oltAPI.setONUState(o.slot, o.port, o.onuId, action);
      toast.success(`ONU ${action}d`);
      fetchONUs(true);
    } catch (err) {
      toast.error(err.response?.data?.message || `${label} failed`);
    } finally {
      setDeactivating(p => { const n = { ...p }; delete n[o.id]; return n; });
    }
  };

  const handleLabelSave = (onuId, newLabel) => {
    setOnus(prev => prev.map(o => o.id === onuId ? { ...o, label: newLabel } : o));
  };

  const handleProfileSave = () => { fetchONUs(true); };

  const oltLink = (path) => oltWebURL ? `${oltWebURL}/action/${path}` : null;

  // ── Save config backup ──
  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      const [cfgRes, sysRes] = await Promise.allSettled([
        settingsAPI.getOLT(),
        oltAPI.getStatus(),
      ]);
      const cfg = cfgRes.status === 'fulfilled' ? cfgRes.value.data?.data : null;
      const sys = sysRes.status === 'fulfilled' ? sysRes.value.data?.data : null;
      const backup = {
        exportedAt: new Date().toISOString(),
        olt: cfg ? {
          ip:         cfg.ip,
          port:       cfg.port,
          username:   cfg.username,
          make:       cfg.make,
          model:      cfg.model,
          deviceName: cfg.deviceName,
          location:   cfg.location,
          useSSL:     cfg.useSSL,
          ponPorts:   cfg.ponPorts,
          power:      cfg.power,
          isEnabled:  cfg.isEnabled,
        } : null,
        systemInfo: sys || null,
        onuCount: totalONUs,
        onlineOnus: onlineONUs,
      };
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `olt-config-backup-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Config backup downloaded');
    } catch (err) {
      toast.error('Failed to export config');
    } finally { setSavingConfig(false); }
  };

  // Bulk version of the per-customer "Fetch from OLT" button on CustomerDetailPage's edit
  // panel — matches every broadband customer's PPPoE username to an ONU in one pass and
  // saves routerSerial/routerModel/ponNumber directly, no per-customer review. Username
  // match only — the slower MAC-based fallback for whatever's left is its own button below.
  const handleFetchAllOlt = async () => {
    setFetchingAllOlt(true);
    setFetchAllOltResult(null);
    try {
      const { data } = await oltAPI.fetchAllBroadbandOlt();
      setFetchAllOltResult(data.data);
      const { updated, total } = data.data;
      if (updated > 0) toast.success(`Updated OLT data for ${updated} of ${total} broadband customers`);
      else toast('No broadband customers matched an ONU', { icon: 'ℹ️' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Fetch failed');
    } finally {
      setFetchingAllOlt(false);
    }
  };

  // Split out from handleFetchAllOlt (2026-09-02 user request) — the MAC-based fallback pass
  // for customers a username match couldn't place. Much slower (one OLT round-trip per
  // leftover customer, 1.5s apart), so it's a deliberate second click rather than always
  // running as part of the fast username-only fetch above.
  // "Match Blank ONUs by MAC" — ONU-first: step 1 previews which blank ONUs can be matched to
  // exactly one active customer (one OLT read, nothing written); step 2 applies the rows the
  // admin leaves ticked, in the background, and the page polls for the result.
  const handleFetchAllOltMac = async () => {
    setFetchingAllOltMac(true);
    setFetchAllOltMacResult(null);
    try {
      const { data } = await oltAPI.fetchAllBroadbandOltMac();
      const result = data.data;
      setFetchAllOltMacResult(result);
      setMacMatchSelected(result.plan.filter(p => p.status === 'proposed').map(p => p.onuId));
      if (!result.blankCount) toast('No blank or stale-named ONUs online right now', { icon: 'ℹ️' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'MAC match preview failed');
    } finally {
      setFetchingAllOltMac(false);
    }
  };

  const handleApplyMacMatch = async () => {
    setApplyingMacMatch(true);
    try {
      await oltAPI.applyBlankOnuMacMatch(macMatchSelected);
      let last = null;
      for (;;) {
        await new Promise(r => setTimeout(r, 5000));
        let status;
        try { ({ data: { data: status } } = await oltAPI.fetchAllBroadbandOltMacStatus()); }
        catch { continue; } // a dropped poll isn't a failed run — keep waiting
        if (status.running) continue;
        last = status.lastResult;
        break;
      }
      if (!last || last.failed) { toast.error(last?.failed || 'Labelling failed'); return; }
      setFetchAllOltMacResult(last);
      setMacMatchSelected([]);
      if (last.aborted) toast.error('Stopped early — the OLT stopped responding');
      else toast.success(`Labelled ${last.labelled} ONU(s)`);
      fetchONUs(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Labelling failed');
    } finally {
      setApplyingMacMatch(false);
    }
  };

  // ── Search + port filter ──
  const filteredONUs = onus.filter(o => {
    if (selectedPort === 'offline') { if (isOnuOnline(o)) return false; }
    else if (selectedPort === 'online') { if (!isOnuOnline(o)) return false; }
    else if (selectedPort === 'weak') { if (!isOnuWeak(o)) return false; }
    else if (selectedPort !== 'all' && o.port !== +selectedPort) return false;
    if (offlineReason && (isOnuOnline(o) || ((o.deregisterReason || '').trim() || 'Unknown') !== offlineReason)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (o.serialNumber || '').toLowerCase().includes(q) ||
      (o.model || '').toLowerCase().includes(q) ||
      (o.label || o.description || o.name || '').toLowerCase().includes(q) ||
      (o.id || '').toLowerCase().includes(q) ||
      String(o.port || '').includes(q)
    );
  });

  const modals = (
    <>
      {showProvision && (
        <ProvisionModal
          onClose={() => setShowProvision(false)}
          onSuccess={fetchONUs}
        />
      )}
      {profileModal && (
        <ProfileModal
          onu={profileModal}
          onClose={() => setProfileModal(null)}
          onSuccess={handleProfileSave}
        />
      )}
      {upgradeModal && (
        <UpgradeModal
          onu={upgradeModal}
          onClose={() => setUpgradeModal(null)}
          onSuccess={() => fetchONUs(true)}
        />
      )}
    </>
  );

  if (isAppView) {
    return (
      <>
        <OltMobileView
          isAdmin={isAdmin} inTechnical={inTechnical}
          configured={configured} sysInfo={sysInfo} updated={updated} errors={errors}
          onus={onus} ports={ports} gePorts={gePorts} alarms={alarms}
          loading={{ onus: onusLoading, ports: portsLoading, ge: geLoading, alarms: alarmsLoading }}
          tab={tab} setTab={setTab} search={search} setSearch={setSearch}
          clearSerialParam={() => { if (searchParams.get('serial')) setSearchParams({}); }}
          onRefresh={{ onus: () => fetchONUs(true), ports: fetchPorts, ge: fetchGEPorts, alarms: fetchAlarms, status: fetchStatus }}
          actions={{
            reboot: handleReboot, deregister: handleDeregister, toggleState: handleToggleState,
            labelSaved: handleLabelSave, openProfile: setProfileModal, openUpgrade: setUpgradeModal,
            openProvision: () => setShowProvision(true),
            openOltConfig: (o) => {
              const url = oltLink(`onuconfigsrv.html?ponid=${o.port}&onuid=${o.onuId}`);
              if (url) window.open(url, 'olt-config', 'width=1100,height=750,scrollbars=yes,resizable=yes');
              else toast.error('OLT web URL not available');
            },
            fetchAll: handleFetchAllOlt, matchMac: handleFetchAllOltMac, applyMac: handleApplyMacMatch,
            saveConfig: handleSaveConfig,
          }}
          busy={{ deactivating, fetchAll: fetchingAllOlt, matchMac: fetchingAllOltMac, applyMac: applyingMacMatch, saveConfig: savingConfig }}
          fetchAllResult={fetchAllOltResult} clearFetchAll={() => setFetchAllOltResult(null)}
          macResult={fetchAllOltMacResult} macSelected={macMatchSelected} clearMac={() => setFetchAllOltMacResult(null)}
          toggleMac={id => setMacMatchSelected(sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id])}
        />
        {modals}
      </>
    );
  }

  // ── Loading / not configured ──
  if (configured === null) {
    return (
      <div className="card" style={{ maxWidth: 400, margin: '40px auto' }}>
        <div className="card-body" style={{ textAlign: 'center', padding: 40 }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--text-muted)' }}>Connecting to OLT…</p>
        </div>
      </div>
    );
  }

  if (configured === false) {
    return (
      <div className="card" style={{ maxWidth: 500, margin: '40px auto' }}>
        <div className="card-body" style={{ textAlign: 'center', padding: 40 }}>
          <Server size={48} style={{ color: 'var(--text-muted)', marginBottom: 16 }} />
          <h3 style={{ marginBottom: 8 }}>OLT Not Configured</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '.9rem', lineHeight: 1.6, marginBottom: 20 }}>
            Enter your SYROTECH OLT connection details in Settings to get started.
          </p>
          {isAdmin
            ? <Link to="/settings" className="btn btn-primary"><Settings size={14} style={{ marginRight: 6 }} />Open Settings</Link>
            : <p style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>Ask your admin to configure the OLT in Settings.</p>
          }
        </div>
      </div>
    );
  }

  return (
    <div className="ow">
      <div className="ow-card ow-head">
        <div className="ow-head-row">
          <span className={`ow-dot ${errors.status ? 'ow-bad' : 'ow-ok'}`} />
          <div className="ow-head-text">
            <b>{sysInfo?.hostname || sysInfo?.model || 'SYROTECH OLT'}</b>
            <span>{[sysInfo?.model && sysInfo.model !== sysInfo.hostname ? sysInfo.model : null, 'OLT device'].filter(Boolean).join(' · ')}</span>
          </div>
          {sysInfo && (
            <div className="ow-vitals">
              {[['Temp', sysInfo.temperature], ['CPU', sysInfo.cpuUsage], ['Memory', sysInfo.memoryUsage], ['Uptime', sysInfo.uptime]].map(([k, v]) => (
                <div className="ow-vital" key={k}><small>{k}</small><b>{v || '—'}</b></div>
              ))}
            </div>
          )}
          <div className="ow-head-side">
            <span className="ow-stamp">
              {errors.status ? <span className="ow-bad-text">Last refresh failed</span> : null}
              {updated.status ? `Updated ${new Date(updated.status).toLocaleTimeString('en-IN', { hour12: false })}` : 'Not loaded yet'}
            </span>
            <button className="btn btn-secondary btn-sm" onClick={fetchStatus} title="Refresh OLT status">
              <RefreshCw size={13} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {totalONUs > 0 && (
        <>
          <div className="ow-card ow-health">
            <div className="ow-health-main">
              <div className="ow-bar" aria-hidden>
                <i style={{ width: `${healthyONUs / totalONUs * 100}%`, background: 'var(--success)' }} />
                <i style={{ width: `${weakONUs / totalONUs * 100}%`, background: 'var(--warning)' }} />
                <i style={{ width: `${offlineONUs / totalONUs * 100}%`, background: 'var(--danger)' }} />
              </div>
              <div className="ow-stats">
                <button className={`ow-stat ow-ok ${selectedPort === 'online' ? 'is-active' : ''}`}
                  onClick={() => { setTab('onus'); setOfflineReason(null); setSelectedPort(p => p === 'online' ? 'all' : 'online'); }}>
                  <b>{onlineONUs}</b><span>Online of {totalONUs}</span>
                </button>
                <button className={`ow-stat ow-bad ${selectedPort === 'offline' ? 'is-active' : ''}`}
                  onClick={() => { setTab('onus'); setOfflineReason(null); setSelectedPort(p => p === 'offline' ? 'all' : 'offline'); }}>
                  <b>{offlineONUs}</b><span>Offline</span>
                </button>
                <button className={`ow-stat ow-warn ${selectedPort === 'weak' ? 'is-active' : ''}`}
                  onClick={() => { setTab('onus'); setOfflineReason(null); setSelectedPort(p => p === 'weak' ? 'all' : 'weak'); }}>
                  <b>{weakONUs}</b><span>Weak signal</span>
                </button>
              </div>
              {offlineReasons.length > 0 && (
                <div className="ow-reasons">
                  <span>Offline cause:</span>
                  {offlineReasons.map(([r, n]) => (
                    <button key={r} className={`ow-chip ${offlineReason === r ? 'is-active' : ''}`}
                      onClick={() => { setTab('onus'); setSelectedPort('offline'); setOfflineReason(cur => cur === r ? null : r); }}>
                      {r} · {n}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="ow-health-chart">
              <HealthDonut healthy={healthyONUs} weak={weakONUs} offline={offlineONUs} />
            </div>
          </div>

          <div className="ow-charts">
            <div className="ow-card ow-chart-card">
              <header><b>PON port usage</b><span>Online vs offline per port</span></header>
              <PonUsageChart portStats={portStats} />
            </div>
            <div className="ow-card ow-chart-card">
              <header><b>Signal strength</b><span>Online ONUs by Rx power</span></header>
              <SignalHistogram onus={onus} />
            </div>
          </div>
        </>
      )}

      {/* ── Main Card ── */}
      <div className="card">
        {/* Tabs header */}
        <div className="card-header olt-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div className="olt-tabs" style={{ display: 'flex', gap: 4 }}>
            {[
              { key: 'onus',    label: 'ONUs',       icon: Wifi },
              { key: 'ge',      label: 'GE Ports',   icon: Activity },
              { key: 'ports',   label: 'PON Ports',  icon: Router },
              { key: 'alarms',  label: 'Alarms',     icon: AlertTriangle },
              { key: 'sysinfo', label: 'System Info',icon: Server },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                style={{
                  padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '.82rem',
                  fontWeight: tab === key ? 600 : 400,
                  background: tab === key ? 'var(--primary)' : 'var(--bg)',
                  color: tab === key ? 'var(--c-ffffff)' : 'var(--text-muted)',
                  display: 'flex', alignItems: 'center', gap: 5,
                }}
              >
                <Icon size={13} /> {label}
                {key === 'alarms' && alarms.length > 0 && <em className="ow-tab-badge">{alarms.length}</em>}
              </button>
            ))}
          </div>
          <div className="olt-actions" style={{ display: 'flex', gap: 8 }}>
            {tab === 'onus' && (
              <div className="ow-toggle" role="group" aria-label="ONU list style">
                <button className={onuView === 'cards' ? 'is-active' : ''} onClick={() => pickOnuView('cards')}>Cards</button>
                <button className={onuView === 'table' ? 'is-active' : ''} onClick={() => pickOnuView('table')}>Table</button>
              </div>
            )}
            {isAdmin && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleFetchAllOlt}
                disabled={fetchingAllOlt}
                title="Match every broadband customer's PPPoE username to an ONU and save its serial/model/PON port"
              >
                <Download size={13} style={{ marginRight: 5 }} />
                {fetchingAllOlt ? 'Fetching…' : 'Fetch All Broadband OLT Data'}
              </button>
            )}
            {isAdmin && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleFetchAllOltMac}
                disabled={fetchingAllOltMac || applyingMacMatch}
                title="Finds online ONUs with no description (or still named after a closed/unknown customer), reads the OLT MAC table once, and previews which active customer is behind each — nothing is written until you Apply"
              >
                <Download size={13} style={{ marginRight: 5 }} />
                {fetchingAllOltMac ? 'Checking…' : 'Match Blank ONUs by MAC'}
              </button>
            )}
            {isAdmin && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleSaveConfig}
                disabled={savingConfig}
                title="Download OLT config backup"
              >
                <Download size={13} style={{ marginRight: 5 }} />
                {savingConfig ? 'Saving…' : 'Backup Config'}
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={() => {
              if (tab === 'onus')    fetchONUs(true);
              else if (tab === 'ge')      fetchGEPorts();
              else if (tab === 'ports')   fetchPorts();
              else if (tab === 'alarms')  fetchAlarms();
              else fetchStatus();
            }}>
              <RefreshCw size={13} style={{ marginRight: 5 }} />Refresh
            </button>
            {isAdmin && tab === 'onus' && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowProvision(true)}>
                <Plus size={13} style={{ marginRight: 5 }} />Provision ONU
              </button>
            )}
          </div>
        </div>

        {fetchAllOltResult && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
            background: 'var(--bg)', borderBottom: '1px solid var(--border)',
            padding: '10px 16px', fontSize: '.82rem',
          }}>
            <span style={{ fontWeight: 700, color: 'var(--c-059669)' }}>{fetchAllOltResult.updated} updated</span>
            {fetchAllOltResult.unmatched > 0 && <span style={{ color: 'var(--c-d97706)' }}>{fetchAllOltResult.unmatched} unmatched</span>}
            <span style={{ color: 'var(--text-muted)' }}>of {fetchAllOltResult.total} broadband customers</span>
            {fetchAllOltResult.errors?.length > 0 && (
              <details style={{ marginLeft: 'auto' }}>
                <summary style={{ cursor: 'pointer', color: 'var(--text-muted)' }}>Show errors</summary>
                <div style={{ maxHeight: 160, overflowY: 'auto', marginTop: 6 }}>
                  {fetchAllOltResult.errors.map((e, i) => (
                    <div key={i} style={{ padding: '3px 0', borderTop: i > 0 ? '1px solid var(--border)' : 'none' }}>
                      <strong>{e.customer}</strong> — {e.error}
                    </div>
                  ))}
                </div>
              </details>
            )}
            <button className="btn btn-secondary btn-sm" onClick={() => setFetchAllOltResult(null)}><X size={12} /></button>
          </div>
        )}

        {fetchAllOltMacResult && (
          <BlankOnuMatchPanel
            result={fetchAllOltMacResult}
            selected={macMatchSelected}
            onToggle={id => setMacMatchSelected(sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id])}
            onApply={handleApplyMacMatch}
            applying={applyingMacMatch}
            onClose={() => setFetchAllOltMacResult(null)}
          />
        )}

        <div className="card-body" style={{ padding: 0 }}>

          {/* ── ONUs Tab ── */}
          {tab === 'onus' && (
            <>
              {/* ── Toolbar: search + PON dropdown ── */}
              <div className="olt-toolbar" style={{
                padding: '12px 20px', borderBottom: '1px solid var(--border)',
                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              }}>
                <div className="olt-search" style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 340 }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
                  <input
                    className="form-control"
                    style={{ paddingLeft: 32, height: 34 }}
                    placeholder="Search serial, name, GPON ID…"
                    value={search}
                    onChange={e => {
                      setSearch(e.target.value);
                      if (!e.target.value && searchParams.get('serial')) setSearchParams({});
                    }}
                  />
                </div>

                {/* PON dropdown */}
                {activePorts.length > 0 && (
                  <select
                    className="form-control"
                    style={{ height: 34, width: 'auto', minWidth: 160, fontSize: '.83rem' }}
                    value={selectedPort}
                    onChange={e => { setSelectedPort(e.target.value); setOfflineReason(null); }}
                  >
                    <option value="all">All PON Ports ({totalONUs} ONUs)</option>
                    <option value="online">All Online ({onlineONUs} ONUs)</option>
                    <option value="offline">All Offline ({offlineONUs} ONUs)</option>
                    <option value="weak">Weak signal ({weakONUs} ONUs)</option>
                    {activePorts.map(p => {
                      const s = portStats[p] || { total: 0, online: 0 };
                      return (
                        <option key={p} value={p}>
                          PON {p} — {s.online}/{s.total} active
                        </option>
                      );
                    })}
                  </select>
                )}

                <span style={{ fontSize: '.8rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {filteredONUs.length}{selectedPort !== 'all' ? ` of ${totalONUs}` : ''} ONUs
                </span>
              </div>

              {onuView === 'cards' && (
                onusLoading ? (
                  <div className="ow-loading"><div className="spinner" /> Loading ONUs…</div>
                ) : filteredONUs.length === 0 ? (
                  <div className="ow-loading">
                    <Wifi size={32} />
                    <div>{totalONUs === 0 ? 'No ONUs found on this OLT' : 'No ONUs match filter'}</div>
                  </div>
                ) : (
                  <div className="ow-onu-grid">
                    {filteredONUs.map((o, i) => {
                      const online = isOnuOnline(o);
                      const weak = isOnuWeak(o);
                      const enabled = isOnuEnabled(o);
                      const tone = !online ? 'ow-bad' : weak ? 'ow-warn' : !enabled ? 'ow-off' : 'ow-ok';
                      return (
                        <div key={o.id || o.onuId || i} className={`ow-card ow-onu ${tone}`}>
                          <div className="ow-onu-head">
                            <div className="ow-onu-title">
                              <b className="ow-mono">{o.serialNumber || o.id || '—'}</b>
                              <small className="ow-mono">{o.id || '—'}{o.model ? ` · ${o.model}` : ''}</small>
                            </div>
                            <SignalBadge dbm={onuRx(o)} />
                          </div>
                          <div className="ow-onu-tags">
                            <span className={`ow-tag ${online ? 'ow-ok' : 'ow-bad'}`}>{online ? 'Online' : 'Offline'}</span>
                            {!enabled && <span className="ow-tag ow-off">Disabled</span>}
                            {weak && <span className="ow-tag ow-warn">Weak signal</span>}
                            {!online && <span className="ow-tag ow-bad">{(o.deregisterReason || '').trim() || 'Unknown'}</span>}
                            {o.port != null && <span className="ow-tag ow-off">PON {o.port}</span>}
                          </div>
                          <div className="ow-onu-meta">
                            {o.linePro && <span>Profile <b>{o.linePro}</b></span>}
                            {!online && o.lastDeregister && <span>Down since <b>{o.lastDeregister}</b></span>}
                          </div>
                          <div className="ow-onu-foot">
                            <LabelCell onu={o} isAdmin={isAdmin} onSave={handleLabelSave} />
                            <OnuActionButtons onu={o} isAdmin={isAdmin} deactivating={deactivating} oltLink={oltLink}
                              onProfile={setProfileModal} onUpgrade={setUpgradeModal} onReboot={handleReboot}
                              onToggle={handleToggleState} onDeregister={handleDeregister} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )}

              {/* ── ONU Table ── */}
              {onuView === 'table' && (
              <div className="table-container">
                {onusLoading ? (
                  <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                    <div className="spinner" style={{ margin: '0 auto 12px' }} />Loading ONUs…
                  </div>
                ) : filteredONUs.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                    <Wifi size={32} style={{ marginBottom: 8 }} />
                    <div>{totalONUs === 0 ? 'No ONUs found on this OLT' : 'No ONUs match filter'}</div>
                  </div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Serial / Model</th>
                        <th>Label</th>
                        <th>GPON ID</th>
                        <th>Status</th>
                        <th>Rx Power</th>
                        <th>Tx Power</th>
                        {isAdmin && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredONUs.map((o, i) => {
                        const isOnline = (o.status || '').toLowerCase().includes('online') || o.online === true;
                        return (
                          <tr key={o.id || o.onuId || i}>
                            <td data-keep className="font-mono olt-num" style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--text-muted)' }}>
                              {o.onuId ?? i + 1}
                            </td>
                            <td data-title>
                              {o.serialNumber
                                ? <div className="font-mono" style={{ fontSize: '.82rem', fontWeight: 600 }}>{o.serialNumber}</div>
                                : <div style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>—</div>
                              }
                              {o.model && (
                                <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{o.model}</div>
                              )}
                            </td>
                            <td data-keep><LabelCell onu={o} isAdmin={isAdmin} onSave={handleLabelSave} /></td>
                            <td>
                              <span className="font-mono" style={{ fontSize: '.82rem', fontWeight: 700 }}>{o.id || '—'}</span>
                            </td>
                            <td data-keep>
                              <span className={`badge badge-${isOnline ? 'active' : 'danger'}`}>
                                {o.status || (isOnline ? 'Online' : 'Offline')}
                              </span>
                            </td>
                            <td><SignalBadge dbm={o.rxPower ?? o.rx_power ?? o.opticalRx} /></td>
                            <td><SignalBadge dbm={o.txPower ?? o.tx_power ?? o.opticalTx} /></td>
                            {isAdmin && (
                              <td>
                                <div className="olt-acts" style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    title="ONU Config (opens OLT interface)"
                                    onClick={() => {
                                      const url = oltLink(`onuconfigsrv.html?ponid=${o.port}&onuid=${o.onuId}`);
                                      if (url) window.open(url, 'olt-config', 'width=1100,height=750,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no');
                                      else toast.error('OLT web URL not available');
                                    }}
                                    style={{ padding: '3px 7px' }}
                                  >
                                    <Settings size={12} />
                                  </button>
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    title={o.linePro ? `Line: ${o.linePro} · Srv: ${o.srvPro || 'N/A'} — click to change` : 'Set Line / Service Profile'}
                                    onClick={() => setProfileModal(o)}
                                    style={{ padding: '3px 7px', display: 'flex', alignItems: 'center', gap: 3 }}
                                  >
                                    <Tag size={12} />
                                    {o.linePro
                                      ? <span style={{ fontSize: '.72rem', maxWidth: 52, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.linePro}</span>
                                      : null
                                    }
                                  </button>
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    title="Upgrade ONU Firmware"
                                    onClick={() => setUpgradeModal(o)}
                                    style={{ padding: '3px 7px' }}
                                  >
                                    <UploadCloud size={12} />
                                  </button>
                                  <button className="btn btn-warning btn-sm" title="Restart ONU" onClick={() => handleReboot(o)} style={{ padding: '3px 7px' }}>
                                    <RotateCcw size={12} />
                                  </button>
                                  <button
                                    className="btn btn-sm"
                                    title={isOnuEnabled(o) ? 'Deactivate ONU' : 'Activate ONU'}
                                    onClick={() => handleToggleState(o)}
                                    disabled={!!deactivating[o.id]}
                                    style={{
                                      padding: '3px 7px',
                                      background: isOnuEnabled(o) ? 'var(--cb-fef3c7)' : 'var(--cb-d1fae5)',
                                      color:      isOnuEnabled(o) ? 'var(--c-92400e)' : 'var(--c-065f46)',
                                      border:     isOnuEnabled(o) ? '1px solid var(--cb-fcd34d)' : '1px solid var(--cb-6ee7b7)',
                                    }}
                                  >
                                    {isOnuEnabled(o) ? <PowerOff size={12} /> : <Power size={12} />}
                                  </button>
                                  <button className="btn btn-danger btn-sm" title="Deregister" onClick={() => handleDeregister(o)} style={{ padding: '3px 7px' }}>
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              )}
            </>
          )}

          {/* ── GE Ports Tab ── */}
          {tab === 'ge' && (
            <div style={{ padding: 24 }}>
              {geLoading ? (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>
                  <div className="spinner" style={{ margin: '0 auto 12px' }} />Loading GE ports…
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: 16, fontSize: '.83rem', color: 'var(--text-muted)' }}>
                    Hover over a port to see traffic statistics.
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: 14,
                  }}>
                    {gePorts.map(p => <GEPortCard key={p.id} port={p} />)}
                  </div>

                  {/* GE port table summary */}
                  {gePorts.some(p => p.rxBytes != null) && (
                    <div style={{ marginTop: 28 }}>
                      <div style={{ fontWeight: 600, marginBottom: 10, fontSize: '.88rem' }}>Traffic Summary</div>
                      <table>
                        <thead>
                          <tr>
                            <th>Port</th>
                            <th>Status</th>
                            <th>Speed</th>
                            <th>↓ Rx</th>
                            <th>↑ Tx</th>
                            <th>Errors</th>
                          </tr>
                        </thead>
                        <tbody>
                          {gePorts.map(p => (
                            <tr key={p.id}>
                              <td style={{ fontWeight: 600 }}>{p.name ? `${p.name} (${p.id})` : p.id}</td>
                              <td>
                                <span className={`badge badge-${(p.status || '').toLowerCase() === 'up' ? 'active' : 'danger'}`}>
                                  {p.status || '—'}
                                </span>
                              </td>
                              <td style={{ fontSize: '.82rem' }}>{p.speed || '—'}</td>
                              <td style={{ fontSize: '.82rem' }}>{p.rxBytes != null ? fmtBytes(p.rxBytes) : '—'}</td>
                              <td style={{ fontSize: '.82rem' }}>{p.txBytes != null ? fmtBytes(p.txBytes) : '—'}</td>
                              <td style={{ fontSize: '.82rem', color: p.errors > 0 ? 'var(--danger)' : undefined }}>
                                {p.errors || 0}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── PON Ports Tab ── */}
          {tab === 'ports' && (
            <div className="table-container">
              {portsLoading ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                  <div className="spinner" style={{ margin: '0 auto 12px' }} />Loading ports…
                </div>
              ) : ports.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>No port data</div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Port</th>
                      <th>Status</th>
                      <th>ONUs Connected</th>
                      <th>ONUs Online</th>
                      <th>Avg Rx Power</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ports.map((p, i) => {
                      const isUp = (p.status || '').toLowerCase().includes('up') || (p.status || '').toLowerCase().includes('active');
                      return (
                        <tr key={p.id || p.portId || i}>
                          <td style={{ fontWeight: 600 }}>PON {p.port ?? p.portId ?? p.id ?? i}</td>
                          <td>
                            <span className={`badge badge-${isUp ? 'active' : 'danger'}`}>
                              {p.status || '—'}
                            </span>
                          </td>
                          <td>{p.totalOnus ?? p.onuCount ?? '—'}</td>
                          <td style={{ color: 'var(--success)' }}>{p.onlineOnus ?? p.activeOnus ?? '—'}</td>
                          <td><SignalBadge dbm={p.avgRxPower ?? p.avgRx} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ── Alarms Tab ── */}
          {tab === 'alarms' && (
            <div className="table-container">
              {alarmsLoading ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                  <div className="spinner" style={{ margin: '0 auto 12px' }} />Loading alarms…
                </div>
              ) : alarms.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                  <Zap size={32} style={{ marginBottom: 8, color: 'var(--success)' }} />
                  <div>No active alarms</div>
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Severity</th>
                      <th>Source</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alarms.map((a, i) => {
                      const sev = (a.severity || a.level || '').toLowerCase();
                      const badgeClass = sev === 'critical' || sev === 'major' ? 'badge-danger'
                        : sev === 'minor' ? 'badge-warning' : 'badge-info';
                      return (
                        <tr key={a.id || i}>
                          <td style={{ fontSize: '.8rem', whiteSpace: 'nowrap' }}>{a.time || a.raisedAt || '—'}</td>
                          <td><span className={`badge ${badgeClass}`}>{a.severity || a.level || '—'}</span></td>
                          <td style={{ fontSize: '.82rem' }}>{a.source || a.object || '—'}</td>
                          <td style={{ fontSize: '.82rem' }}>{a.type || a.alarmType || '—'}</td>
                          <td style={{ fontSize: '.82rem', color: 'var(--text-muted)' }}>{a.description || a.detail || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ── System Info Tab ── */}
          {tab === 'sysinfo' && (
            <div style={{ padding: 24 }}>
              {!sysInfo ? (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>
                  <button className="btn btn-secondary" onClick={fetchStatus}>
                    <RefreshCw size={14} style={{ marginRight: 6 }} />Load System Info
                  </button>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                  {Object.entries(sysInfo).map(([key, val]) => (
                    <div key={key} style={{ background: 'var(--bg)', borderRadius: 8, padding: '12px 16px', border: '1px solid var(--border)' }}>
                      <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}>
                        {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                      </div>
                      <div style={{ fontWeight: 500, fontSize: '.9rem', wordBreak: 'break-all' }}>
                        {typeof val === 'object' ? JSON.stringify(val) : String(val ?? '—')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {modals}
    </div>
  );
}
